import React, { useState, useEffect, useCallback } from 'react';
import { BTN_XS } from '../constants/buttons';
import { sanitiseAnswers } from '../constants/raReviewQuestions';

/**
 * Pre/Post-Assessment Review block — INNER content only.
 *
 * Renders a set of advisory yes/no questions with a gate-question pattern:
 * if the first question is answered NO, subsequent questions are hidden and
 * the gate's noOutcome is surfaced inline. Every NO answer surfaces its
 * question's noOutcome below the pills in an amber callout. Sign-off is
 * never blocked — this is decision capture, not enforcement.
 *
 * The outer card / title / collapse chrome is owned by the caller (typically
 * <CollapsibleCard>) so this component only renders the questions.
 *
 * Props:
 *  - questions: array from raReviewQuestions.js (first item is the gate)
 *  - answers: { [key]: { answer: 'yes'|'no'|null, note: string } } — controlled
 *  - canEdit: bool
 *  - onChange(nextAnswers): fires on every keystroke/click
 *  - onCommit(nextAnswers): fires on radio click + textarea blur (deferred persist)
 *  - testIdPrefix: 'pre' | 'post' (for data-testid hooks)
 */
export default function RaReviewSection({
  questions,
  answers,
  canEdit,
  onChange,
  onCommit,
  testIdPrefix = 'review',
}) {
  // Every hook runs BEFORE the empty-list guard (react-hooks/rules-of-hooks,
  // enforced by src/eslintGate.test.js).
  const setAnswer = useCallback(
    (key, patch) => {
      const current = answers?.[key] || { answer: null, note: '' };
      const merged = { ...current, ...patch };
      const next = sanitiseAnswers({ ...(answers || {}), [key]: merged });
      // Sanitise drops the entry if it'd be empty after merge — restore the
      // merged value back into the next state so the click registers even
      // when answer is null + note is blank (transitional state).
      if (!next[key] && (merged.answer || merged.note)) {
        next[key] = { answer: merged.answer || null, note: merged.note || '' };
      }
      if (onChange) onChange(next);
      if (onCommit) onCommit(next);
    },
    [answers, onChange, onCommit]
  );

  if (!Array.isArray(questions) || questions.length === 0) return null;
  const gate = questions[0];
  const rest = questions.slice(1);
  const gateAnswer = answers?.[gate.key]?.answer ?? null;
  // Which gate answer expands the follow-up questions. Defaults to 'yes' (the
  // pre-review pattern: "is this elevated?" YES → ask more). Post-review
  // residual_risk overrides to 'no' (semantic flip: "is residual low enough?"
  // NO → ask the justification question).
  const showRestOn = gate.showRestOn || 'yes';

  return (
    <div data-testid={`ra-detail-${testIdPrefix}-review-section`}>
      {gate.intro && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{gate.intro}</p>
      )}
      <ReviewQuestion
        question={gate}
        value={answers?.[gate.key]}
        canEdit={canEdit}
        onAnswer={(a) => setAnswer(gate.key, { answer: a })}
        onNote={(n) => setAnswer(gate.key, { note: n })}
        testIdPrefix={`${testIdPrefix}-${gate.key}`}
      />
      {gateAnswer === showRestOn &&
        rest.map((q) => (
          <ReviewQuestion
            key={q.key}
            question={q}
            value={answers?.[q.key]}
            canEdit={canEdit}
            onAnswer={(a) => setAnswer(q.key, { answer: a })}
            onNote={(n) => setAnswer(q.key, { note: n })}
            testIdPrefix={`${testIdPrefix}-${q.key}`}
          />
        ))}
      {questions
        .filter((q) => q.noBottomNotice && (answers?.[q.key]?.answer === 'no'))
        .map((q) => (
          <div
            key={`notice-${q.key}`}
            className="mt-4 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3"
            role="alert"
            data-testid={`${testIdPrefix}-${q.key}-bottom-notice`}
          >
            <div className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-0.5">
              {q.noBottomNotice.title}
            </div>
            <div className="text-xs text-amber-800 dark:text-amber-200">
              {q.noBottomNotice.body}
            </div>
          </div>
        ))}
    </div>
  );
}

function ReviewQuestion({ question, value, canEdit, onAnswer, onNote, testIdPrefix }) {
  const answer = value?.answer ?? null;
  // Local note state so typing isn't laggy; commit on blur.
  const [note, setNote] = useState(value?.note || '');
  useEffect(() => {
    setNote(value?.note || '');
  }, [value?.note]);

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 first:mt-0 first:pt-0 first:border-t-0">
      <div className="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">
        {question.label}
      </div>
      <p className="text-sm text-gray-800 dark:text-gray-200">{question.question}</p>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <div className="flex gap-1.5 shrink-0" role="radiogroup" aria-label={question.label}>
          <PillButton
            label="Yes"
            active={answer === 'yes'}
            intent="confirm"
            disabled={!canEdit}
            onClick={() => onAnswer(answer === 'yes' ? null : 'yes')}
            testId={`${testIdPrefix}-yes`}
          />
          <PillButton
            label="No"
            active={answer === 'no'}
            intent="danger-soft"
            disabled={!canEdit}
            onClick={() => onAnswer(answer === 'no' ? null : 'no')}
            testId={`${testIdPrefix}-no`}
          />
        </div>
        {answer === 'no' && (
          <div
            className={`rounded-md border text-xs px-3 py-1.5 ${
              question.noTone === 'positive'
                ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200'
                : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'
            }`}
            data-testid={`${testIdPrefix}-outcome`}
          >
            {question.noOutcome}
          </div>
        )}
      </div>
      <textarea
        className="mt-2 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-base md:text-sm text-gray-800 dark:text-gray-200 px-2 py-1.5 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
        rows={2}
        placeholder="Optional notes"
        value={note}
        disabled={!canEdit}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => {
          if ((value?.note || '') !== note) onNote(note);
        }}
        data-testid={`${testIdPrefix}-note`}
      />
    </div>
  );
}

function PillButton({ label, active, intent, disabled, onClick, testId }) {
  // Active state uses the intent's full styling; inactive state uses secondary
  // (gray) so the unselected option doesn't shout.
  const activeClass = BTN_XS[intent] || BTN_XS.secondary;
  const inactiveClass = BTN_XS.secondary;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`${active ? activeClass : inactiveClass} min-w-[56px] min-h-[44px] md:min-h-0 justify-center`}
      data-testid={testId}
    >
      {label}
    </button>
  );
}
